import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Sparkles,
  CheckCircle2,
  ArrowRight,
  ShieldAlert,
  ShieldCheck,
  Upload,
  Lock,
  FileText,
  Gauge,
  Target,
  Building2,
  AlertTriangle,
  ChevronDown,
  Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { BuyReadinessDialog } from "@/components/visa-readiness/BuyReadinessDialog";

const READINESS_FAQ = [
  {
    q: "Is my personal & DS-160 data safe?",
    a: "Yes. Every submission is encrypted with 256-bit SSL, stored under strict RLS in a locked backend, never sold or shared, and purged from working storage after your report is generated.",
  },
  {
    q: "How is this different from ChatGPT?",
    a: "Our audit engine is calibrated on 1,500+ real WeLivdIt consular interview logs, Indian consulate-specific refusal trends, and a deterministic debt-to-income scoring model - then narrated by AI.",
  },
  {
    q: "Can I re-scan my profile after fixing my red flags?",
    a: "Yes - that's exactly what the Dual Scan (Rs.500) and Pro Expert Bundle (Rs.1,200) are built for. Run a first scan, fix your DS-160 / funding / SOP based on the flags, then re-scan.",
  },
  {
    q: "How long does the audit take?",
    a: "About 2 minutes of inputs, then ~30 seconds for the AI to render your full report and PDF.",
  },
];

export const Route = createFileRoute("/visa-readiness/")({
  head: () => ({
    meta: [
      { title: "AI F-1 Visa Profile Audit - Approval Odds & 214(b) Red Flags | WeLivdIt" },
      {
        name: "description",
        content:
          "Know your F-1 visa approval odds and 214(b) red flags before the interview. AI + Consular Specialist profile audit in 2 minutes. DS-160 mismatch, debt-to-income and mock question analysis from Rs.300.",
      },
      { property: "og:title", content: "AI F-1 Visa Profile Audit - WeLivdIt" },
      {
        property: "og:description",
        content: "Get your consular approval index, DS-160 audit and 5 tailored mock questions in under 2 minutes.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://welivdit.com/visa-readiness" },
      { property: "og:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "AI F-1 Visa Profile Audit - WeLivdIt" },
      {
        name: "twitter:description",
        content: "Consular approval index, DS-160 audit and 5 tailored mock questions in under 2 minutes.",
      },
    ],
    links: [{ rel: "canonical", href: "https://welivdit.com/visa-readiness" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: READINESS_FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "AI F-1 Visa Profile Audit",
          brand: { "@type": "Brand", name: "WeLivdIt" },
          description:
            "AI + Consular Specialist F-1 visa profile audit with approval index, 214(b) red flags and mock question analysis.",
          offers: [
            { "@type": "Offer", name: "Single Scan", price: "300", priceCurrency: "INR" },
            { "@type": "Offer", name: "Dual Scan", price: "500", priceCurrency: "INR" },
            { "@type": "Offer", name: "Pro Bundle", price: "1200", priceCurrency: "INR" },
          ],
        }),
      },
    ],
  }),
  component: VisaReadinessLanding,
});

type ReadinessPlan = "readiness_1" | "readiness_2" | "readiness_3";

function VisaReadinessLanding() {
  const [buyOpen, setBuyOpen] = useState<ReadinessPlan | null>(null);

  return (
    <div className="bg-background">
      {/* ============ HERO ============ */}
      <HeroSection onBuy={setBuyOpen} />

      {/* ============ BEFORE / AFTER MATRIX ============ */}
      <ContrastMatrix />

      {/* ============ WHAT'S INSIDE THE REPORT ============ */}
      <InsideReport />

      {/* ============ PRICING ============ */}
      <PricingTiers onBuy={setBuyOpen} />

      {/* ============ FAQ ============ */}
      <FaqSection />

      {/* ============ REFUSAL UPSELL ============ */}
      <RefusalUpsellBanner />

      {buyOpen && (
        <BuyReadinessDialog
          open={buyOpen !== null}
          onOpenChange={(v) => setBuyOpen(v ? buyOpen : null)}
          plan={buyOpen}
        />
      )}
    </div>
  );
}

// ============================================================
// HERO
// ============================================================
function HeroSection({ onBuy }: { onBuy: (plan: ReadinessPlan) => void }) {
  return (
    <section className="relative overflow-hidden bg-gradient-hero">
      <div className="mx-auto grid max-w-6xl gap-10 px-5 pt-14 pb-16 md:grid-cols-[1.15fr_1fr] md:pt-20 md:pb-24">
        {/* Copy */}
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            <Sparkles className="h-3.5 w-3.5" /> AI + Consular Specialist Audit
          </span>
          <h1 className="mt-5 font-display text-4xl font-semibold leading-[1.08] tracking-tight md:text-5xl lg:text-[3.4rem]">
            Know Your F-1 Visa Approval Odds & <span className="text-primary">214(b) Red Flags</span> - Before Walking
            into the Consulate.
          </h1>
          <p className="mt-5 max-w-xl text-lg text-foreground/70">
            Get an AI + Consular Specialist profile audit in 2 minutes. Detect DS-160 mismatches, debt-to-income
            coverage gaps, and personalized high-risk mock questions.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={() => onBuy("readiness_1")}
              className="group relative inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground shadow-glow transition hover:-translate-y-0.5 hover:opacity-95"
            >
              <span className="absolute inset-0 rounded-full bg-primary opacity-70 animate-ping" aria-hidden />
              <span className="relative inline-flex items-center gap-2">
                Scan My Visa Profile (₹300) <ArrowRight className="h-4 w-4" />
              </span>
            </button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/visa-readiness/assessment">
                <Upload className="mr-2 h-4 w-4" />
                Upload DS-160 / Resume to Fast-Track
              </Link>
            </Button>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground/60">
            <span className="inline-flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5" /> 256-bit SSL · data deleted post-report
            </span>
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" /> 1,500+ approvals of Specialist experience
            </span>
          </div>
        </div>

        {/* Live simulator widget */}
        <LiveScoreWidget />
      </div>
    </section>
  );
}

function LiveScoreWidget() {
  const target = 66;
  const [score, setScore] = useState(0);
  const [step, setStep] = useState(0);
  const badges = [
    { icon: AlertTriangle, tone: "amber", label: "DS-160 Conflict Detected" },
    { icon: AlertTriangle, tone: "amber", label: "Sponsor ITR vs. Loan Ratio Flag" },
    { icon: Lock, tone: "primary", label: "5 Tailored Mock Questions Generated" },
  ] as const;

  useEffect(() => {
    let raf: number;
    const start = performance.now();
    const duration = 1600;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setScore(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setStep((s) => (s + 1) % (badges.length + 1)), 1400);
    return () => clearInterval(id);
  }, [badges.length]);

  const circumference = 2 * Math.PI * 52;
  const dash = (score / 100) * circumference;
  const tone = score >= 75 ? "#16a34a" : score >= 50 ? "#ca8a04" : "#dc2626";

  return (
    <div className="relative">
      <div className="rounded-2xl border border-border/70 bg-card/90 p-5 shadow-xl backdrop-blur">
        <div className="flex items-center justify-between text-xs">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 font-medium text-primary">
            <Sparkles className="h-3 w-3" /> Live Sample Audit
          </span>
          <span className="text-muted-foreground"> SJSU · 70L Loan · 2× Refusal</span>
        </div>

        <div className="mt-4 flex items-center gap-5">
          {/* Gauge */}
          <div className="relative h-32 w-32 shrink-0">
            <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
              <circle cx="60" cy="60" r="52" fill="none" stroke="currentColor" strokeOpacity="0.12" strokeWidth="10" />
              <circle
                cx="60"
                cy="60"
                r="52"
                fill="none"
                stroke={tone}
                strokeWidth="10"
                strokeLinecap="round"
                strokeDasharray={`${dash} ${circumference}`}
                style={{ transition: "stroke 300ms ease" }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-display text-3xl font-bold" style={{ color: tone }}>
                {score}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">/ 100</span>
            </div>
          </div>

          <div className="flex-1">
            <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
              Consular Approval Index
            </p>
            <p className="mt-1 text-sm text-foreground/80">
              Moderate risk profile. 214(b) triggers detected in DS-160 and funding narrative.
            </p>
          </div>
        </div>

        {/* Animated badge stack */}
        <div className="mt-5 space-y-2">
          {badges.map((b, i) => {
            const visible = step > i || step === 0;
            const Icon = b.icon;
            const cls =
              b.tone === "amber"
                ? "border-amber-400/40 bg-amber-50 text-amber-800"
                : "border-primary/30 bg-primary/10 text-primary";
            return (
              <div
                key={b.label}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs transition-all duration-500 ${cls}`}
                style={{
                  opacity: visible ? 1 : 0.35,
                  transform: visible ? "translateX(0)" : "translateX(-4px)",
                }}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="font-medium">{b.label}</span>
              </div>
            );
          })}
        </div>

        <div className="mt-4 rounded-lg bg-muted/60 p-3 text-[11px] text-muted-foreground">
          Preview only. Your live audit runs on your actual profile inputs.
        </div>
      </div>

      <div className="pointer-events-none absolute -bottom-4 -right-4 -z-10 h-40 w-40 rounded-full bg-primary/20 blur-3xl" />
    </div>
  );
}

// ============================================================
// BEFORE / AFTER CONTRAST
// ============================================================
function ContrastMatrix() {
  const bad = [
    "Blind to hidden 214(b) Immigrant Intent triggers",
    "Memorized YouTube answers penalized by Consular Officers",
    "Unchecked DS-160 vs. Financial Sponsor ITR conflicts",
    "$185 Visa Fee lost + 6-month delay on re-application",
  ];
  const good = [
    "Complete 214(b) Immigrant Intent & Refusal Risk Index",
    "5 Custom Mock Questions based on your exact profile (gaps, funding, refusals)",
    "DS-160 Consistency & Debt-to-Sponsor Income Audit",
    "Tailored Defense Frameworks for Mumbai, Hyderabad, Delhi & Chennai consulates",
  ];

  return (
    <section className="border-y bg-muted/30">
      <div className="mx-auto max-w-6xl px-5 py-16 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
            Why 70% of applicants get rejected
          </p>
          <h2 className="mt-3 font-display text-3xl font-semibold md:text-4xl">
            The real cost of walking in unprepared.
          </h2>
          <p className="mt-3 text-foreground/70">
            Two applicants, same university, same funds. What separates them is not luck - it's the audit.
          </p>
        </div>

        <div className="mt-10 grid gap-5 md:grid-cols-2">
          <div className="rounded-2xl border-2 border-[#dc2626]/25 bg-[#fff5f5] p-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#b91c1c]">The "Black Box" Walk-in</p>
            <h3 className="mt-1 font-display text-xl font-semibold text-[#7f1d1d]">Without WeLivdIt Audit</h3>
            <ul className="mt-4 space-y-3 text-sm">
              {bad.map((b) => (
                <li key={b} className="flex gap-2 text-[#7f1d1d]">
                  <span className="mt-0.5 select-none">❌</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary">The "Prepared" Walk-in</p>
            <h3 className="mt-1 font-display text-xl font-semibold">With WeLivdIt Profile Audit</h3>
            <ul className="mt-4 space-y-3 text-sm">
              {good.map((g) => (
                <li key={g} className="flex gap-2">
                  <span className="mt-0.5 select-none">✅</span>
                  <span>{g}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// INSIDE THE REPORT
// ============================================================
function InsideReport() {
  const modules = [
    {
      icon: Gauge,
      emoji: "📊",
      title: "Consular Approval Index",
      body: "0-100 score with color-coded breakdown across academic, financial, DS-160 and 214(b) risk.",
    },
    {
      icon: ShieldAlert,
      emoji: "⚠️",
      title: "DS-160 Discrepancy Audit",
      body: "Sponsor ITR-to-Loan ratio, income vs. funding coverage, and work-history mismatches.",
    },
    {
      icon: Target,
      emoji: "🎯",
      title: "5 High-Risk Mock Questions",
      body: "Tailored to your gaps, funding and refusal history - with non-scripted defense strategy.",
    },
    {
      icon: Building2,
      emoji: "🏛️",
      title: "Consulate Scrutiny Index",
      body: "Mumbai, Hyderabad, Delhi and Chennai trends - so you know what your VO actually watches for.",
    },
    {
      icon: FileText,
      emoji: "📄",
      title: "Official Academic PDF",
      body: "Downloadable executive report with Document ID, applicant metadata and page-break-safe layout.",
    },
  ];

  return (
    <section className="mx-auto max-w-6xl px-5 py-16 md:py-20">
      <div className="mx-auto max-w-2xl text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Inside your report</p>
        <h2 className="mt-3 font-display text-3xl font-semibold md:text-4xl">
          Five modules that read like a consular file.
        </h2>
        <p className="mt-3 text-foreground/70">
          Every scan generates the same academic-grade output. No gated sections, no upsells inside the report.
        </p>
      </div>

      <div className="mt-10 grid gap-4 md:grid-cols-3">
        {modules.map((m, i) => (
          <div
            key={m.title}
            className={`group rounded-2xl border border-border bg-card p-6 transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lg ${
              i === 0 ? "md:col-span-2" : ""
            }`}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">{m.emoji}</span>
              <m.icon className="h-5 w-5 text-primary" />
            </div>
            <h3 className="mt-4 font-display text-lg font-semibold">{m.title}</h3>
            <p className="mt-2 text-sm text-foreground/70">{m.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ============================================================
// PRICING
// ============================================================
function PricingTiers({ onBuy }: { onBuy: (plan: ReadinessPlan) => void }) {
  const tiers: {
    key: ReadinessPlan;
    name: string;
    price: string;
    tag?: string;
    tone: "default" | "popular" | "expert";
    bullets: string[];
    cta: string;
  }[] = [
    {
      key: "readiness_1",
      name: "Single AI Scan",
      price: "₹300",
      tone: "default",
      cta: "Get 1 Scan - ₹300",
      bullets: [
        "1 Full AI Visa Profile Assessment",
        "Complete 214(b) Red Flag Audit & DS-160 Check",
        "5 Predicted Mock Questions & Strategy Tips",
        "Official Downloadable Academic PDF Report",
      ],
    },
    {
      key: "readiness_2",
      name: "Dual Scan Starter",
      price: "₹500",
      tag: "⭐ Most Popular · Best Value",
      tone: "popular",
      cta: "Get 2 Scans - ₹500",
      bullets: [
        "2 Full AI Visa Profile Scans - re-scan after fixing red flags",
        "Complete AI Audit Reports & PDF Exports",
        "Track your score improvement before your interview",
        "Everything in Single AI Scan, x2",
      ],
    },
    {
      key: "readiness_3",
      name: "Pro Expert Bundle",
      price: "₹1,200",
      tag: "Highest Approval Rate",
      tone: "expert",
      cta: "Get Expert Bundle - ₹1,200",
      bullets: [
        "4 Full AI Visa Profile Scans",
        "All AI Reports + Academic PDF Exports",
        "1-on-1 Human Specialist Review (written audit within 24-48 hrs)",
        "Priority WhatsApp Support",
      ],
    },
  ];

  return (
    <section id="pricing" className="bg-muted/30">
      <div className="mx-auto max-w-6xl px-5 py-16 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Credit tiers</p>
          <h2 className="mt-3 font-display text-3xl font-semibold md:text-4xl">
            Pick the audit depth that fits your profile.
          </h2>
          <p className="mt-3 text-foreground/70">
            Every tier ships the full report - PDF, mock questions, DS-160 audit and consulate index.
          </p>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {tiers.map((t) => {
            const highlight = t.tone === "popular";
            const expert = t.tone === "expert";
            return (
              <div
                key={t.key}
                className={`relative flex flex-col rounded-2xl border bg-card p-6 transition hover:-translate-y-1 hover:shadow-xl ${
                  highlight
                    ? "border-2 border-primary shadow-lg md:scale-[1.03]"
                    : expert
                      ? "border-2 border-amber-400/50"
                      : "border-border"
                }`}
              >
                {t.tag && (
                  <span
                    className={`absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-semibold ${
                      highlight ? "bg-primary text-primary-foreground" : "bg-amber-400 text-amber-950"
                    }`}
                  >
                    {t.tag}
                  </span>
                )}
                <h3 className="font-display text-xl font-semibold">{t.name}</h3>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-display text-4xl font-bold">{t.price}</span>
                  <span className="text-xs text-muted-foreground">one-time · INR</span>
                </div>
                <ul className="mt-5 flex-1 space-y-2.5 text-sm">
                  {t.bullets.map((b) => (
                    <li key={b} className="flex gap-2">
                      <CheckCircle2
                        className={`mt-0.5 h-4 w-4 shrink-0 ${expert ? "text-amber-600" : "text-primary"}`}
                      />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className={`mt-6 w-full ${expert ? "bg-amber-500 text-amber-950 hover:bg-amber-500/90" : ""}`}
                  variant={highlight ? "default" : expert ? "default" : "outline"}
                  onClick={() => onBuy(t.key)}
                >
                  {expert && <Star className="mr-1 h-4 w-4" />} {t.cta}
                </Button>
              </div>
            );
          })}
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Secure checkout via Razorpay · GST invoice on request · Credits activated instantly
        </p>
      </div>
    </section>
  );
}

// ============================================================
// FAQ
// ============================================================
function FaqSection() {
  return (
    <section className="mx-auto max-w-3xl px-5 py-16 md:py-20">
      <div className="text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">FAQ</p>
        <h2 className="mt-3 font-display text-3xl font-semibold md:text-4xl">Answers before you buy.</h2>
      </div>

      <div className="mt-8 space-y-3">
        {READINESS_FAQ.map((f, i) => (
          <FaqItem key={f.q} q={f.q} a={f.a} defaultOpen={i === 0} />
        ))}
      </div>
    </section>
  );
}

function FaqItem({ q, a, defaultOpen = false }: { q: string; a: string; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-2xl border bg-card">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="font-medium">{q}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <div className="border-t px-5 py-4 text-sm text-foreground/75">{a}</div>}
    </div>
  );
}

// ============================================================
// REFUSAL UPSELL
// ============================================================
function RefusalUpsellBanner() {
  return (
    <section className="px-5 pb-20">
      <div className="mx-auto max-w-5xl overflow-hidden rounded-3xl border-2 border-[#dc2626]/30 bg-gradient-to-br from-[#fff1f2] via-[#fef2f2] to-[#fff7ed] p-8 md:p-10">
        <div className="flex flex-col items-start gap-6 md:flex-row md:items-center">
          <div className="flex-1">
            <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#b91c1c]">
              🔥 Had a previous 214(b) refusal?
            </p>
            <h3 className="mt-3 font-display text-2xl font-semibold text-[#7f1d1d] md:text-3xl">
              Generic AI tools are not enough for repeat attempts.
            </h3>
            <p className="mt-3 text-sm text-[#7f1d1d]/85 md:text-base">
              Practice live with senior specialists who analyze <em>why</em> your previous attempt failed and rebuild
              your defense narrative from scratch - not from a script.
            </p>
          </div>
          <div className="shrink-0">
            <Button size="lg" asChild className="bg-[#b91c1c] hover:bg-[#991b1b]">
              <Link to="/refusal-support">
                Explore Live Human Mock &amp; Refusal Recovery (₹4,500 - ₹7,000) <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="mt-2 text-center text-[11px] text-[#7f1d1d]/70">
              or{" "}
              <Link to="/pricing" className="underline underline-offset-2">
                view all mock packages
              </Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
